/**
 * Раздел «Сельское хозяйство» страницы «Аналитика» (US-17, задача
 * DP-426) — новый состав виджетов из трёх частей сверху вниз (FR-1.2):
 * **сводный модуль** (плашка из пяти плиток — объём продукции, ИФО,
 * растениеводство, животноводство, производители), **детальный модуль**
 * (карточка «Сельское хозяйство» из пяти блоков, `AgricultureDetail`) и
 * **два сохранённых виджета** US-04.5 — «Структура посевных площадей» и
 * «Животноводство и птицеводство».
 *
 * Состав US-04.5 этим US **заменён**, а не дополнен (FR-1.1): показатель
 * «Самообеспеченность продуктами питания» и виджеты «Ежегодное
 * производство мяса» и «Производство молочной продукции» сняты с
 * подраздела. Виджет «Динамика поголовья» снят следом (правка DP-426):
 * поголовье остаётся на экране только в «Животноводстве и
 * птицеводстве».
 *
 * Сводный модуль и каждый блок грузятся **своей операцией** (US-18
 * FR-1.1): валовый выпуск и производство животноводства публикуются
 * ежемесячно, поголовье — ежемесячно на дату, производители — раз в
 * полгода, посевная площадь — раз в год, валовой сбор — по итогам
 * уборки. Отчётная подпись у сводного модуля своя и на блоки не
 * распространяется (FR-2.7, FR-8.1).
 *
 * Количество действующих производителей на пятой плитке берётся из
 * операции блока производителей (US-18 FR-5.5): два независимых расчёта
 * одной величины рано или поздно разойдутся.
 *
 * Контракт шести операций — `digital-passport-v2/docs/US-18_agriculture_api.md`
 * (задача бэка DP-427). Величины приходят со своей единицей и охватом,
 * подписи — парой ru/kz, период — реквизитами; приведение к примитивам
 * экрана делает `useAgricultureView`. Флаг `widget_enabled` ответа — тот
 * же каталог витрины, что читает `ModuleGate`, поэтому вторым гейтом он
 * здесь не дублируется; `level_supported` и `has_data` — разные
 * состояния и показываются разными текстами (US-18 FR-4).
 *
 * **Сохранённые виджеты** (FR-12) остаются в прежнем составе и на
 * прежнем источнике — ручном вводе (FR-12.5), поэтому их данные
 * по-прежнему идут из `/analytics/agriculture` (демо-КАТО — моки
 * hardData). Их подписи объясняют расхождение с новыми блоками: площадь
 * идёт в укрупнённых группах и тыс. га по всем категориям хозяйств, а
 * поголовье — годовыми значениями в разрезе КХ/ТОО и ЛПХ (FR-12.4).
 *
 * Drill-through на странице нет ни у одного элемента (FR-10.1), включая
 * сохранённые виджеты: «Структура посевных площадей» прежний переход к
 * первичным данным тоже теряет (FR-10.3).
 */
import { Wheat } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AgricultureDetail } from './AgricultureDetail';
import { useAgricultureView } from './agricultureView';
import {
  PeriodCaption,
  fmt1Fixed,
  useStatBlock,
  useStatText,
} from './statValue';
import {
  StatCard,
  type StatCardFootnote,
} from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import { WidgetErrorBoundary } from '../../../components/common/WidgetErrorBoundary';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import analyticsAgriculture from '../../../hardData/analyticsAgriculture.json';
import {
  getAnalyticsAgriculture,
  getAnalyticsAgricultureCropArea,
  getAnalyticsAgricultureCropYield,
  getAnalyticsAgricultureHeadcount,
  getAnalyticsAgricultureProducers,
  getAnalyticsAgricultureProduction,
  getAnalyticsAgricultureSummary,
} from '../../../requests/analytics.request';
import { ModuleGate } from '../../../components/common/ModuleGate';
import { MODULES } from '../../../consts/modules';
import { useKato } from '../../../contexts/KatoContext';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type {
  AgricultureAnalyticsResponse,
  AgricultureCropAreaResponse,
  AgricultureCropYieldResponse,
  AgricultureGrossRow,
  AgricultureHeadcountResponse,
  AgricultureLivestockProductionResponse,
  AgricultureProducersResponse,
  AgricultureSummaryResponse,
  StatIfo,
  StatValue,
} from '../../../types/analytics';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
/** Сумма компонент; все null → null (в плашке показывается «—»). */
const sum = (...parts: Array<number | null>): number | null => {
  const known = parts.filter((v): v is number => v !== null);
  return known.length === 0 ? null : known.reduce((acc, v) => acc + v, 0);
};

const cell = (v: number | null): string => (v !== null ? fmt(v) : '—');

/** crop ответа API → ключ подписи sownAreas.volumeRows.*. */
const CROP_KEYS: Record<string, string> = {
  grains_legumes: 'grainAndLegumes',
  oilseeds: 'oilseeds',
  fodder: 'fodder',
  potato: 'potato',
  vegetables: 'vegetables',
};

/** animal ответа API → ключ подписи husbandry.species.*. */
const ANIMAL_KEYS: Record<string, string> = {
  cattle: 'cattle',
  small_cattle: 'sheep',
  horses: 'horses',
  birds: 'poultry',
  camels: 'camels',
  pigs: 'pigs',
};

/** Строка таблиц виджета: вид + колонки разреза (`null` → «—»). */
interface HusbandryTableRow {
  key: string;
  khToo: number | null;
  lph: number | null;
  total: number | null;
  meatDirection?: number | null;
}

interface HusbandryTableProps {
  title: string;
  testId: string;
  rows: HusbandryTableRow[];
  /** Подпись вида по ключу номенклатуры (виды скота / виды птицы). */
  label: (key: string) => string;
  /** Пятая колонка «в т.ч. мясного направления» — только у животноводства. */
  withMeatDirection?: boolean;
}

/**
 * Таблица разреза поголовья: вид · КХ/ТОО · ЛПХ · Всего (+ мясное
 * направление у животноводства). Обе таблицы виджета одинаковы по
 * построению и различаются только последней колонкой, поэтому это один
 * компонент с флагом, а не две копии вёрстки.
 */
const HusbandryTable = ({
  title,
  testId,
  rows,
  label,
  withMeatDirection = false,
}: HusbandryTableProps) => {
  const { t } = useTranslation();
  const numeric = 'h-auto whitespace-nowrap px-4 py-2.5 text-right font-bold';

  return (
    <div
      data-testid={testId}
      className="overflow-hidden rounded-xl border border-surface-border"
    >
      <div className="border-b border-surface-border bg-surface-muted/60 px-4 py-3 text-sm font-extrabold leading-snug">
        {title}
      </div>
      {/* Широкая таблица скроллится сама (примитив ui/table): пять колонок
          в половине ширины карточки на планшете не помещаются. */}
      <Table>
        <TableHeader>
          <TableRow className="border-surface-border hover:bg-transparent">
            <TableHead className="h-auto px-4 py-2.5 font-bold text-text">
              {t('analytics.agriculture.husbandry.columnKind')}
            </TableHead>
            <TableHead className={cn(numeric, 'text-text')}>
              {t('analytics.agriculture.husbandry.columnKhToo')}
            </TableHead>
            <TableHead className={cn(numeric, 'text-text')}>
              {t('analytics.agriculture.husbandry.columnLph')}
            </TableHead>
            <TableHead className={cn(numeric, 'text-text')}>
              {t('analytics.agriculture.husbandry.columnTotal')}
            </TableHead>
            {withMeatDirection && (
              // Единственная подпись в две строки на макете — перенос по
              // словам, а не `whitespace-nowrap`: иначе колонка выдавит
              // таблицу в горизонтальный скролл уже на десктопе.
              <TableHead className="h-auto min-w-28 px-4 py-2.5 text-right font-bold leading-snug text-text">
                {t('analytics.agriculture.husbandry.columnMeatDirection')}
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              data-testid={`${testId}-${row.key}`}
              className="border-surface-border hover:bg-transparent"
            >
              <TableCell className="px-4 py-3 font-semibold leading-snug">
                {label(row.key)}
              </TableCell>
              <TableCell className="px-4 py-3 text-right tabular-nums">
                {cell(row.khToo)}
              </TableCell>
              <TableCell className="px-4 py-3 text-right tabular-nums">
                {cell(row.lph)}
              </TableCell>
              <TableCell className="px-4 py-3 text-right tabular-nums">
                {cell(row.total)}
              </TableCell>
              {withMeatDirection && (
                <TableCell className="px-4 py-3 text-right tabular-nums">
                  {cell(row.meatDirection ?? null)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

interface SummaryTileVM {
  key: string;
  title: string;
  /** null — данных нет: плитка остаётся на месте с подписью «нет данных». */
  value: string | null;
  unit: string | null;
  footnotes: StatCardFootnote[];
  trend: { down: boolean; danger: boolean } | null;
}

interface AgricultureSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевые виджеты — «Структура
   * посевных площадей» и блок животноводства/птицеводства (FR-2.2). */
  summary?: boolean;
}

export const AgricultureSection = ({
  summary = false,
}: AgricultureSectionProps = {}) => {
  const { t } = useTranslation();
  const { selectedKato } = useKato();
  const text = useStatText();
  const view = useAgricultureView();

  // Видимость сводного модуля и каждого блока задаёт супер-администратор
  // (US-09 FR-1, US-18 FR-11.2): выключенный блок не рендерится вовсе,
  // места не занимает и состоянием «нет данных» не подменяется (FR-9.3).
  const showSummary = useModulePresent(MODULES.analytics.agricultureSummary);
  const showProducers = useModulePresent(
    MODULES.analytics.agricultureProducers,
  );
  const showProduction = useModulePresent(
    MODULES.analytics.agricultureProduction,
  );
  const showHeads = useModulePresent(MODULES.analytics.agricultureHeads);
  const showSownArea = useModulePresent(MODULES.analytics.agricultureSownArea);
  const showYield = useModulePresent(MODULES.analytics.agricultureYield);

  // Сводный модуль и пять блоков — своя операция у каждого (US-18
  // FR-1.1). Выключенный и не показываемый в сводной витрине блок не
  // запрашивается. Детальный модуль целиком — только в подразделе.
  const summaryBlock = useStatBlock<AgricultureSummaryResponse>(
    getAnalyticsAgricultureSummary,
    showSummary && !summary,
  );
  // Производители нужны и блоку, и пятой плитке плашки: величина одна и
  // приходит из одной операции (US-18 FR-5.5).
  const producers = useStatBlock<AgricultureProducersResponse>(
    getAnalyticsAgricultureProducers,
    (showProducers || showSummary) && !summary,
  );
  const production = useStatBlock<AgricultureLivestockProductionResponse>(
    getAnalyticsAgricultureProduction,
    showProduction && !summary,
  );
  const heads = useStatBlock<AgricultureHeadcountResponse>(
    getAnalyticsAgricultureHeadcount,
    showHeads && !summary,
  );
  const sownArea = useStatBlock<AgricultureCropAreaResponse>(
    getAnalyticsAgricultureCropArea,
    showSownArea && !summary,
  );
  const cropYield = useStatBlock<AgricultureCropYieldResponse>(
    getAnalyticsAgricultureCropYield,
    showYield && !summary,
  );

  const { data, loading, isHardKato } =
    useAnalyticsSection<AgricultureAnalyticsResponse>(
      'agriculture',
      getAnalyticsAgriculture,
    );

  // --- Разрезы в общей форме: мок для демо-КАТО,
  // /analytics/agriculture для остальных ---

  const cropStructure: Array<{
    key: string;
    area: number | null;
    gross: number | null;
  }> = isHardKato
    ? analyticsAgriculture.cropStructure
    : (data?.sown_areas ?? []).map((row) => ({
        key: CROP_KEYS[row.crop] ?? row.crop,
        area: row.area_ths_ha,
        gross: row.gross_harvest_ths_t,
      }));

  // «Всего» — готовое из ответа; сумма КХ/ТОО и ЛПХ — фолбэк для
  // демо-мока и старых ответов. Величины `breeding`/`cows` (DP-267) на
  // экран больше не идут: колонок «в т.ч. племенные», «доля племенных» и
  // подстроки «в том числе коровы» на макете виджета нет.
  const livestock: Array<{
    key: string;
    khToo: number | null;
    lph: number | null;
    total: number | null;
    meatDirection: number | null;
  }> = isHardKato
    ? analyticsAgriculture.livestock.map((row) => ({
        key: row.key,
        khToo: row.khToo,
        lph: row.lph,
        total: sum(row.khToo, row.lph),
        meatDirection: row.meatDirection,
      }))
    : (data?.livestock ?? []).map((row) => ({
        key: ANIMAL_KEYS[row.animal] ?? row.animal,
        khToo: row.kh_too,
        lph: row.lph,
        total: row.total ?? sum(row.kh_too, row.lph),
        meatDirection: row.meat_direction,
      }));

  const poultry: Array<{
    key: string;
    khToo: number | null;
    lph: number | null;
    total: number | null;
  }> = isHardKato
    ? analyticsAgriculture.poultry.map((row) => ({
        key: row.key,
        khToo: row.khToo,
        lph: row.lph,
        total: sum(row.khToo, row.lph),
      }))
    : (data?.poultry ?? []).map((row) => ({
        key: row.kind,
        khToo: row.kh_too,
        lph: row.lph,
        total: row.total ?? sum(row.kh_too, row.lph),
      }));

  // --- «Структура посевных площадей» (FR-3.2) — таблица макета: группа
  // культур · посевная площадь (тыс. га) · валовый сбор (тыс. тонн).
  // Итоговая строка, бары и доли сняты вместе с прежним составом
  // виджета; количество товаропроизводителей показывает плашка US-17
  // (FR-2.6), и вторым местом на странице оно не дублируется.
  // Группа без обеих величин строкой не становится: пустая строка под
  // названием культуры читается как ноль.
  const cropRows = cropStructure.flatMap((row) => {
    if (row.area === null && row.gross === null) return [];
    return [{ key: row.key, area: row.area, gross: row.gross }];
  });

  // --- «Животноводство и птицеводство» (FR-3.3) — две таблицы макета ---
  // Разрез КХ/ТОО · ЛПХ · Всего у обеих; у животноводства пятой колонкой
  // идёт мясное направление. «Всего» берётся готовым из ответа, сумма
  // двух категорий — фолбэк для демо-мока и старых ответов.
  const husbandryAnimals = livestock.map((row) => ({
    key: row.key,
    khToo: row.khToo,
    lph: row.lph,
    total: row.total,
    meatDirection: row.meatDirection,
  }));

  const poultryKinds = poultry.map((row) => ({
    key: row.key,
    khToo: row.khToo,
    lph: row.lph,
    total: row.total,
  }));

  // Разбивка птицы по видам живёт только в `screening.agriculture`, а он
  // на стендах пуст: в проде приходит один готовый `poultry_total`
  // (DP-135). Без этой строки таблица «Птицеводство» была бы пустой при
  // живых данных, поэтому итог выводится строкой «Птица всех видов» —
  // но только когда разбивки нет, иначе он задвоил бы её.
  const poultryTotal = isHardKato
    ? null
    : (data?.poultry_total ?? null);
  const poultryRows =
    poultryKinds.length > 0
      ? poultryKinds
      : poultryTotal !== null
        ? [
            {
              key: 'poultryAll',
              khToo: null,
              lph: null,
              total: poultryTotal,
            },
          ]
        : [];

  // --- Сводный модуль (FR-2): пять плиток ---

  /** Подпись ИФО плитки объёма: красная при отрицательной динамике. */
  const ifoFootnote = (ifo: StatIfo | null): StatCardFootnote[] => {
    if (ifo === null || ifo.value === null || ifo.state !== 'value') return [];
    const grew = ifo.value >= 100;
    const good = ifo.direction === 'up_is_bad' ? !grew : grew;
    return [
      {
        // ИФО печатается как есть, в процентах (119,7 %, а не +19,7 %):
        // пересчёта в прирост на экране нет (FR-2.5).
        text: [
          `${t('analytics.stats.ifoShort')} ${fmt1Fixed(ifo.value)}%`,
          ifo.base_label,
        ]
          .filter((part): part is string => part !== null)
          .join(' '),
        bold: ifo.direction === null || good,
        danger: ifo.direction !== null && !good,
      },
    ];
  };

  /**
   * Плитка строится всегда — плашка состоит из пяти фиксированных плиток
   * (замечание по DP-426). Показателя нет — остаётся название и серая
   * подпись «нет данных» (режим `value: null` у `StatCard`): пропавшая
   * плитка читается как «показателя не существует», а не как «данных
   * пока не пришло».
   */
  const tile = (
    key: string,
    title: string,
    value: StatValue | null,
    footnotes: StatCardFootnote[],
    format: (v: number) => string = fmt1Fixed,
  ): SummaryTileVM => {
    if (
      value === null ||
      value.state === 'no_data' ||
      value.state === 'not_applicable'
    ) {
      return {
        key,
        title,
        value: null,
        unit: null,
        footnotes: [],
        trend: null,
      };
    }
    const shown = text(value, format);
    return {
      key,
      title,
      value: shown.text,
      unit: shown.known ? value.unit : null,
      footnotes: shown.known ? footnotes : [],
      trend: null,
    };
  };

  /**
   * Плитка ИФО: значение источника в процентах, стрелка — по отклонению
   * от 100 % (FR-2.5). «Рост — это хорошо или плохо» берётся из
   * `direction` ответа и в вёрстку не зашивается.
   */
  const ifoTile = (ifo: StatIfo | null): SummaryTileVM => {
    const title = t('analytics.agriculture.summary.ifo');
    if (ifo === null || ifo.value === null || ifo.state !== 'value') {
      return {
        key: 'ifo',
        title,
        value: null,
        unit: null,
        footnotes: [],
        trend: null,
      };
    }
    const grew = ifo.value >= 100;
    const good = ifo.direction === 'up_is_bad' ? !grew : grew;
    return {
      key: 'ifo',
      title,
      value: fmt1Fixed(ifo.value),
      unit: '%',
      // База сравнения — готовая подпись ответа, вычитанием года экран
      // её не собирает (US-18 FR-2.5).
      footnotes: ifo.base_label !== null ? [{ text: ifo.base_label }] : [],
      trend: { down: !grew, danger: ifo.direction !== null && !good },
    };
  };

  const summaryData = summaryBlock.data;
  /**
   * Разрез плашки по коду, а не по номеру строки ответа (US-18 FR-5.3):
   * порядок разрезов задаёт источник, и позиционная выборка склеила бы
   * растениеводство с животноводством при первом же его изменении.
   * `additional_cuts` («только сельское хозяйство») на плашку не идут
   * намеренно (FR-5.4) — это другое число под похожей подписью.
   */
  const cutOf = (cut: string): AgricultureGrossRow | null =>
    (summaryData?.rows ?? []).find((row) => row.cut === cut) ?? null;
  const totalCut = cutOf('TOTAL');
  const cropsCut = cutOf('CROP');
  const livestockCut = cutOf('LIVESTOCK');

  const summaryTiles: SummaryTileVM[] = [
    tile(
      'total',
      t('analytics.agriculture.summary.total'),
      view.measure(totalCut?.volume),
      ifoFootnote(view.ifo(totalCut?.ifo)),
    ),
    ifoTile(view.ifo(totalCut?.ifo)),
    tile(
      'crops',
      t('analytics.agriculture.summary.crops'),
      view.measure(cropsCut?.volume),
      ifoFootnote(view.ifo(cropsCut?.ifo)),
    ),
    tile(
      'livestock',
      t('analytics.agriculture.summary.livestock'),
      view.measure(livestockCut?.volume),
      ifoFootnote(view.ifo(livestockCut?.ifo)),
    ),
    // Пятая плитка — итог блока производителей; вторым расчётом на
    // экране он не дублируется (FR-2.6).
    tile(
      'producers',
      t('analytics.agriculture.summary.producers'),
      view.measure(producers.data?.total),
      [{ text: t('analytics.agriculture.summary.producersNote') }],
      fmt,
    ),
  ];

  /**
   * Уровень территории не обслуживается (US-18 FR-4): по сельскому округу
   * и селу показатель не ведётся вовсе, и плашка из пяти «нет данных»
   * обещала бы цифры, которых не будет.
   */
  const summaryUnsupported = summaryData?.level_supported === false;

  // Плашка ждёт обе свои операции: количество производителей приходит из
  // операции блока производителей (US-18 FR-5.5).
  const summaryLoading = summaryBlock.loading || producers.loading;

  return (
    <section className="space-y-4" data-testid="analytics-agriculture">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.agriculture.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Wheat
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Сводный модуль (FR-2). Своя операция — лоадер сохранённых
          виджетов его не задерживает; в сводной витрине «Все»
          показываются только ключевые виджеты темы (US-04.1 FR-2.2). */}
      {!summary && showSummary && (
        <WidgetErrorBoundary
          key={`agriculture-summary-${selectedKato ?? 'none'}`}
        >
          {summaryLoading ? (
            <BlockLoader />
          ) : summaryUnsupported ? (
            <p
              data-testid="agriculture-summary-level-unsupported"
              className="text-sm lowercase text-text-muted"
            >
              {t('analytics.agriculture.levelNotSupported')}
            </p>
          ) : (
            <div className="space-y-2.5" data-testid="agriculture-summary">
              {/* Подпись относится только к сводному модулю: у блоков
                  детального модуля периоды свои (FR-2.7, FR-8.1). */}
              <PeriodCaption
                prefix={t('analytics.agriculture.summary.caption')}
                period={view.period(summaryData?.period)}
                testId="agriculture-summary-period"
              />
              {/* Плиток всегда пять: без данных плитка остаётся на месте
                  с названием и подписью «нет данных» — ряд не
                  перестраивается от того, что выпуск ещё не вышел. */}
              <div className="flex flex-wrap gap-3.5">
                {summaryTiles.map((card) => (
                  // min-w-40, а не обычные min-w-52: пять плиток должны
                  // встать в один ряд уже на 1280 (FR-2.1), где под
                  // контент остаётся ~884 px.
                  <div key={card.key} className="min-w-40 flex-1">
                    <StatCard
                      testId={`analytics-card-agriculture-${card.key}`}
                      title={card.title}
                      value={card.value}
                      unit={card.unit}
                      trend={card.trend}
                      footnotes={card.footnotes}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </WidgetErrorBoundary>
      )}

      {/* Детальный модуль (FR-3 … FR-7) — пять блоков в одной карточке. */}
      {!summary && (
        <WidgetErrorBoundary
          key={`agriculture-detail-${selectedKato ?? 'none'}`}
        >
          <AgricultureDetail
            producers={producers}
            production={production}
            heads={heads}
            sownArea={sownArea}
            cropYield={cropYield}
            show={{
              producers: showProducers,
              production: showProduction,
              heads: showHeads,
              sownArea: showSownArea,
              cropYield: showYield,
            }}
          />
        </WidgetErrorBoundary>
      )}

      {/* Сохранённые виджеты подраздела (FR-12): прежний состав на
          прежнем источнике — ручном вводе (FR-12.5). Пока их данные
          грузятся — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>
          <ModuleGate id={MODULES.analytics.agricultureSownAreas}>
          <Card className="shadow-card">
            <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0">
              <CardTitle>
                {t('analytics.agriculture.sownAreas.title')}
              </CardTitle>
              {/* Источник виджета — ручной ввод, а не статистика БНС
                  (FR-12.4.3): два соседних числа по одной теме из разных
                  источников без такой подписи выглядят как ошибка. */}
              <span className="shrink-0 text-sm text-text-faint">
                {t('analytics.agriculture.manualSource')}
              </span>
            </CardHeader>
            <CardContent>
              {cropRows.length === 0 ? (
                <p
                  data-testid="block-placeholder"
                  className="text-sm lowercase text-text-muted"
                >
                  {t('common.noData')}
                </p>
              ) : (
                <div data-testid="analytics-sown-areas">
                  {/* Единицы объявлены в заголовках колонок, а не
                      отдельной строкой над таблицей: площадь в тыс. га,
                      сбор в тыс. тонн — одна подпись на обе не годится. */}
                  <div className="overflow-hidden rounded-xl border border-surface-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-surface-border bg-surface-muted/60 hover:bg-transparent">
                          <TableHead className="h-auto px-4 py-3 font-bold text-text">
                            {t('analytics.agriculture.sownAreas.columnCrop')}
                          </TableHead>
                          <TableHead className="h-auto whitespace-nowrap px-4 py-3 text-right font-bold text-text">
                            {t('analytics.agriculture.sownAreas.columnArea')}
                          </TableHead>
                          <TableHead className="h-auto whitespace-nowrap px-4 py-3 text-right font-bold text-text">
                            {t('analytics.agriculture.sownAreas.columnGross')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cropRows.map((row) => (
                          <TableRow
                            key={row.key}
                            data-testid={`analytics-sown-areas-${row.key}`}
                            className="border-surface-border even:bg-surface-muted/40 hover:bg-transparent"
                          >
                            <TableCell className="px-4 py-3 font-semibold leading-snug">
                              {t(
                                `analytics.agriculture.sownAreas.crops.${row.key}`,
                              )}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-right tabular-nums">
                              {row.area !== null ? fmt1Fixed(row.area) : '—'}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-right tabular-nums">
                              {row.gross !== null ? fmt1Fixed(row.gross) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Площадь идёт на странице дважды, и расхождение
                      объясняется подписью, а не выравниванием значений
                      (FR-12.4.1). */}
                  <p className="mt-3 text-xs leading-snug text-text-faint">
                    {t('analytics.agriculture.sownAreas.manualNote')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          </ModuleGate>

          <ModuleGate id={MODULES.analytics.agricultureLivestock}>
          <Card className="shadow-card" data-testid="analytics-agri-livestock">
            <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0">
              <CardTitle>
                {t('analytics.agriculture.husbandry.combinedTitle')}
              </CardTitle>
              {/* Источник виджета — ручной ввод (FR-12.4.3). */}
              <span className="shrink-0 text-sm text-text-faint">
                {t('analytics.agriculture.manualSource')}
              </span>
            </CardHeader>
            <CardContent>
              {husbandryAnimals.length === 0 && poultryRows.length === 0 ? (
                <p
                  data-testid="block-placeholder"
                  className="text-sm lowercase text-text-muted"
                >
                  {t('common.noData')}
                </p>
              ) : (
                <>
                  {/* Две таблицы рядом — только от xl: на планшете под
                      контент ~670 px, и пять колонок в половине ширины
                      не читаются. */}
                  <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
                    {husbandryAnimals.length > 0 && (
                      <HusbandryTable
                        testId="analytics-agri-livestock-animals"
                        title={t(
                          'analytics.agriculture.husbandry.livestockTableTitle',
                        )}
                        rows={husbandryAnimals}
                        label={(key) =>
                          t(`analytics.agriculture.husbandry.species.${key}`)
                        }
                        withMeatDirection
                      />
                    )}
                    {poultryRows.length > 0 && (
                      <HusbandryTable
                        testId="analytics-agri-livestock-poultry"
                        title={t(
                          'analytics.agriculture.husbandry.poultryTableTitle',
                        )}
                        rows={poultryRows}
                        // Виды птицы и фолбэк-строка «Птица всех видов»
                        // лежат в одном словаре подписей.
                        label={(key) =>
                          t(`analytics.agriculture.husbandry.table.${key}`)
                        }
                      />
                    )}
                  </div>
                  {/* Поголовье идёт на странице дважды: здесь — годовые
                      значения в разрезе КХ/ТОО и ЛПХ, в блоке выше —
                      состояние на дату по всем категориям (FR-12.4.2). */}
                  <p className="mt-3 text-xs leading-snug text-text-faint">
                    {t('analytics.agriculture.husbandry.manualNote')}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
          </ModuleGate>

        </>
      )}
    </section>
  );
};
